import type { CurrencyIdentifier } from "@opensea/app-kit"
import { head } from "es-toolkit/array"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import { useCallback, useEffect, useState } from "react"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import {
  ActionTimeline,
  ActionTimelineError,
  ActionTimelineSkeleton,
} from "../../../../../components/ActionTimeline"
import { CrossEcosystemSwapNotification } from "../../../../../components/CrossEcosystemSwapNotification"
import type { WalletDestination } from "../../../../../components/DestinationWalletSelect/types"
import {
  PriceChangeModal,
  PriceChangeWarning,
  type PriceValidationResult,
} from "../../../../../components/PriceChange"
import { useAddressCompatibility } from "../../../../../hooks/useAddressCompatibility"
import { useAddress } from "../../../../../polychain/hooks"
import type { SchedulableResponse } from "../../../../../polychain/hooks/useScheduler"
import { useTrackEvent } from "../../../../../providers/TrackEventProvider"
import type { TransactionIdentifier } from "../../../../../stores/currentTransactionStore"
import { FlowActions, FlowState } from "../../../../../stores/flowStore"
import type { CheckoutFlowState } from "../../../../flow-state"
import { useActionErrors } from "../../../../hooks/useErrors"
import { useCardCheckout } from "../../../../PaymentSection/use-card-checkout"
import { itemQuantitiesForQuote } from "../../../../utils/itemQuantitiesForQuote"
import { useBuyItemQuery } from "../../hooks/useBuyItemQuery"
import { CheckoutTimeline_quoteFragment } from "./CheckoutTimeline.fragment"

type CheckoutTimelineProps = {
  quote: FragmentOf<typeof CheckoutTimeline_quoteFragment>
  priceValidation: PriceValidationResult
  selectedCurrency: CurrencyIdentifier | undefined
  selectedCurrencyLoading: boolean
  walletDestination?: WalletDestination
  onAllActionsCompleted?: (
    response: SchedulableResponse,
    receiptTransactions: TransactionIdentifier[],
  ) => void
}

export function CheckoutTimeline({
  quote: quoteKey,
  priceValidation,
  selectedCurrency,
  selectedCurrencyLoading,
  walletDestination,
  onAllActionsCompleted: onAllActionsCompletedProp,
}: CheckoutTimelineProps) {
  const t = useTranslations("wallet.CheckoutTimeline")
  const INSUFFICIENT_FUNDS_ERROR = t("insufficientFundsError")
  const { flow } = useSnapshot(FlowState)
  const _isClaim = (flow as CheckoutFlowState)?.isClaim ?? false
  const actionError = useActionErrors()
  const currentAddress = useAddress()

  const quote = readFragment(CheckoutTimeline_quoteFragment, quoteKey)

  const chainIdentifier =
    quote.totalPrice.pricePerToken[0]?.token.chain.identifier

  const isAddressCompatible = useAddressCompatibility(
    walletDestination?.address ?? currentAddress,
    chainIdentifier,
  )

  const itemQuantities = itemQuantitiesForQuote(quote)

  // ---------------------------------------------------------------------------
  // Skip the Buy Item query when the quote contains only **one** entry and that
  // entry is not an `ItemQuote` (i.e. it came back as `ItemQuoteNotFound` or
  // `ItemNotFound`). In that situation the UI already shows the red “Unavailable`
  // badge, and there is nothing left to purchase, so there is no reason to make
  // the follow-up `buyItems` request.
  // ---------------------------------------------------------------------------
  const shouldSkipBuyItemQuery =
    itemQuantities.length === 1 && !itemQuantities[0].pricePerItem

  const crossChainRequirements =
    actionError?.includes(INSUFFICIENT_FUNDS_ERROR) &&
    quote.supportsRelayerFulfillment?.crossChain

  // Price validation
  const [userAcknowledgedPriceChange, setUserAcknowledgedPriceChange] =
    useState(false)

  const { actions, error, fetching, refetch } = useBuyItemQuery({
    itemQuantities,
    slippage: flow?.type === "checkout" ? flow.slippage : undefined,
    currency: selectedCurrency,
    recipient: walletDestination?.address,
    supportsCrossChain: Boolean(quote.supportsRelayerFulfillment?.crossChain),
    // Pause the query for cross chain buy until the quote is successful
    // Also pause if prices have changed and user hasn't acknowledged
    pause:
      shouldSkipBuyItemQuery ||
      crossChainRequirements ||
      (priceValidation.hasPriceChanged && !userAcknowledgedPriceChange) ||
      !isAddressCompatible ||
      selectedCurrencyLoading,
  })

  // Set crossChain flag accurately based on whether payment currency lives on a different chain than the NFT.
  useEffect(() => {
    if (selectedCurrency) {
      const isCross = selectedCurrency.chainIdentifier !== chainIdentifier
      FlowActions.setFlowField<CheckoutFlowState>("crossChain", isCross)
    }
  }, [selectedCurrency, chainIdentifier])

  const onAllActionsCompleted = useCallback(
    (
      response: SchedulableResponse,
      receiptTransactions: TransactionIdentifier[],
    ) => {
      if (response.action === "TRANSACTION") {
        FlowActions.setFlowField<CheckoutFlowState>("transactionIdentifier", {
          chain: response.transactionIdentifier.chain,
          transactionHash: response.transactionIdentifier.hash,
        })
      }

      onAllActionsCompletedProp?.(response, receiptTransactions)
    },
    [onAllActionsCompletedProp],
  )

  const cardCheckout = useCardCheckout()

  const handleRetry = useCallback(() => {
    if (flow?.type === "checkout" && flow.paymentMethod === "card") {
      cardCheckout()
      return
    }
    refetch()
  }, [refetch, flow, cardCheckout])

  const [showPriceChangeModal, setShowPriceChangeModal] = useState(false)

  const trackEvent = useTrackEvent()
  const onInlineConfirm = () => {
    trackEvent("Inline Price Changed Warning Confirmed", {
      priceValidation,
      flow: "checkout",
    })
    setShowPriceChangeModal(true)
  }

  const onModalConfirm = () => {
    trackEvent("Modal Price Changed Warning Confirmed", {
      priceValidation,
      flow: "checkout",
    })
    setUserAcknowledgedPriceChange(true)
    setShowPriceChangeModal(false)
  }

  if (fetching || selectedCurrencyLoading) {
    return <ActionTimelineSkeleton count={1} />
  }

  if (!(isAddressCompatible || shouldSkipBuyItemQuery)) {
    return <CrossEcosystemSwapNotification />
  }

  // Show price change warning if prices have changed and user hasn't acknowledged
  if (priceValidation.hasPriceChanged && !userAcknowledgedPriceChange) {
    return (
      <>
        <PriceChangeWarning
          onContinue={onInlineConfirm}
          priceValidation={priceValidation}
        />
        {showPriceChangeModal && (
          <PriceChangeModal
            onCancel={() => setShowPriceChangeModal(false)}
            onConfirm={onModalConfirm}
            priceValidation={priceValidation}
            symbol={head(quote.totalPrice.pricePerToken)?.token.symbol}
          />
        )}
      </>
    )
  }

  if (error) {
    return (
      <ActionTimelineError
        message={t("somethingWentWrong")}
        onRetry={handleRetry}
      />
    )
  }

  if (actionError) {
    return <ActionTimelineError message={actionError} onRetry={handleRetry} />
  }

  return (
    <ActionTimeline
      actions={actions}
      errorMessage={actions.length === 0 ? t("noActions") : undefined}
      insufficientFundsOnChain={
        actionError?.includes(INSUFFICIENT_FUNDS_ERROR)
          ? itemQuantities[0].item.chain
          : undefined
      }
      onAllActionsCompleted={onAllActionsCompleted}
      onReload={() => {
        if (actions.length === 0 && shouldSkipBuyItemQuery) {
          return
        }
        handleRetry()
      }}
    />
  )
}
